import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, Timestamp, writeBatch
} from 'firebase/firestore';
import {
  sendPasswordResetEmail,
  getAuth
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../config/firebase';
import { User, CreateUserDto, UpdateUserDto, UserRole } from '../types/user.types';
import { contractService } from './contract.service';
import { trainingService } from './training.service';

// Create user using secondary auth instance (secure frontend method)
export async function createUser(userData: CreateUserDto): Promise<User> {
  const currentAuth = getAuth();
  const currentUser = currentAuth.currentUser;
  
  if (!currentUser) {
    throw new Error('No authenticated user');
  }

  // Verify the current user has permission to create users
  const currentUserProfile = await getUserById(currentUser.uid);
  if (!currentUserProfile) {
    throw new Error('User profile not found');
  }

  const allowedCreatorRoles: UserRole[] = ['provider_admin', 'provider_user', 'client_admin'];
  if (!allowedCreatorRoles.includes(currentUserProfile.role)) {
    throw new Error('You do not have permission to create users');
  }

  // Validate role hierarchy
  const allowedToCreate: Record<UserRole, UserRole[]> = {
    'provider_admin': ['field', 'provider_user', 'client_admin', 'client_user'],
    'provider_user': ['field', 'client_admin', 'client_user'],
    'client_admin': ['client_user'],
    'client_user': [],
    'field': []
  };

  if (!allowedToCreate[currentUserProfile.role].includes(userData.role)) {
    throw new Error(`You cannot create users with role: ${userData.role}`);
  }

  try {
    // Generate temporary password
    const tempPassword = userData.password || generateTempPassword();

    // Create the user SERVER-SIDE via the createUser callable. The users
    // collection is rules-locked (`allow create: if false` — cloud functions
    // only), so the function creates the Auth account, the role claim, AND the
    // profile document. It also adopts orphaned Auth accounts left behind by
    // the old browser-side flow (Auth created, doc write denied).
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const createUserFn = httpsCallable(getFunctions(), 'createUser');
    const result: any = await createUserFn({
      email: userData.email,
      password: tempPassword,
      name: userData.name,
      role: userData.role,
      organizationId: userData.organizationId || null,
      organizationName: userData.organizationName || '',
      projects: userData.projects || [],
      phoneNumber: userData.phoneNumber || '',
      ...(typeof userData.payRate === 'number' && isFinite(userData.payRate) ? { payRate: userData.payRate } : {}),
    });
    const newUser = { uid: result.data.uid as string };

    // Local mirror of the doc the function wrote (for the return value and
    // downstream contract/training assignment).
    const userDoc: User = {
      uid: newUser.uid,
      email: userData.email,
      name: userData.name,
      role: userData.role,
      organizationId: userData.organizationId || null,
      organizationName: userData.organizationName || '',
      projects: userData.projects || [],
      phoneNumber: userData.phoneNumber || '',
      status: 'active',
      isActive: true,
      settings: { theme: 'light', language: 'en' },
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: currentUser.uid
    };
    
    // Send password reset email if requested
    if (userData.sendPasswordReset !== false) {
      try {
        await sendPasswordResetEmail(auth, userData.email);
      } catch (error) {
        console.error('Error sending password reset:', error);
      }
    }

    // Auto-assign mandatory contracts and training (onboarding)
    if (userDoc.organizationId) {
      // Assign mandatory contracts
      try {
        await contractService.assignMandatoryContractsToUser(
          newUser.uid,
          userDoc.name,
          userDoc.organizationId,
          currentUser.uid,
          currentUserProfile.name || currentUserProfile.email || 'Admin'
        );
      } catch (error) {
        console.error('Error assigning mandatory contracts:', error);
        // Don't fail user creation if contract assignment fails
      }

      // Assign required training courses
      try {
        await trainingService.assignRequiredCoursesToUser(
          newUser.uid,
          userDoc.name,
          userDoc.organizationId,
          currentUser.uid,
          currentUserProfile.name || currentUserProfile.email || 'Admin'
        );
      } catch (error) {
        console.error('Error assigning training courses:', error);
        // Don't fail user creation if training assignment fails
      }
    }

    return userDoc;
    
  } catch (error: any) {
    console.error('Error creating user:', error);
    
    throw error;
  }
}

function generateTempPassword(): string {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = 'Aa1!'; // Ensure it meets requirements
  
  for (let i = 4; i < 12; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  
  return password;
}

export async function getUsers(filters?: any): Promise<User[]> {
  const currentAuth = getAuth();
  const currentUser = currentAuth.currentUser;
  
  if (!currentUser) {
    throw new Error('No authenticated user');
  }
  
  // Get current user profile to check their role
  const currentUserProfile = await getUserById(currentUser.uid);
  
  let q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
  
  // Filter by organization for non-provider users
  if (currentUserProfile && currentUserProfile.role !== 'provider_admin' && currentUserProfile.role !== 'provider_user') {
    q = query(q, where('organizationId', '==', currentUserProfile.organizationId));
  }

  if (filters?.role) {
    q = query(q, where('role', '==', filters.role));
  }
  if (filters?.organizationId) {
    q = query(q, where('organizationId', '==', filters.organizationId));
  }
  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ ...doc.data() } as User));
}

export async function getUserById(uid: string): Promise<User | null> {
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return docSnap.data() as User;
  }
  return null;
}

export async function updateUser(uid: string, data: UpdateUserDto): Promise<void> {
  try {
    const userRef = doc(db, 'users', uid);
    
    const updateData: Record<string, any> = {
      updatedAt: Timestamp.now(),
      ...Object.fromEntries(
        Object.entries(data).filter(([key, v]) => v !== undefined && key !== 'projects')
      ),
    };

    if (data.projects !== undefined) {
      updateData.projects = Array.isArray(data.projects) ? data.projects : [];
    }
    
    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error in updateUser:', error);
    throw error;
  }
}

export async function toggleUserStatus(uid: string, isActive: boolean): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, {
    isActive,
    status: isActive ? 'active' : 'inactive',
    updatedAt: Timestamp.now()
  });
}

export async function bulkUpdateUsers(userIds: string[], updates: Partial<User>): Promise<void> {
  const batch = writeBatch(db);
  
  userIds.forEach(uid => {
    const userRef = doc(db, 'users', uid);
    batch.update(userRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  });
  
  await batch.commit();
}

export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

// Set user password directly (admin only, via Cloud Function)
export async function setUserPassword(targetUserId: string, newPassword: string): Promise<void> {
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../config/firebase');
  const setPasswordFn = httpsCallable(functions, 'setUserPassword');
  await setPasswordFn({ targetUserId, newPassword });
}

// Delete user completely (provider_admin only) - Uses Cloud Function to delete from Auth + Firestore
export async function deleteUser(uid: string): Promise<void> {
  const currentAuth = getAuth();
  const currentUser = currentAuth.currentUser;

  if (!currentUser) {
    throw new Error('No authenticated user');
  }

  // Check if current user is provider_admin
  const currentUserProfile = await getUserById(currentUser.uid);
  if (currentUserProfile?.role !== 'provider_admin') {
    throw new Error('Only provider admins can delete users');
  }

  try {
    // Use the pre-configured functions instance from firebase config
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../config/firebase');
    const deleteUserCompletelyFn = httpsCallable(functions, 'deleteUserCompletely');

    // Call Cloud Function to delete from Auth + Firestore + cleanup
    await deleteUserCompletelyFn({ userId: uid });
  } catch (error: any) {
    console.error('Error calling deleteUserCompletely:', error);

    // Fallback: If Cloud Function fails, at least delete Firestore doc
    if (error.code === 'functions/not-found' || error.code === 'functions/unavailable') {
      await deleteDoc(doc(db, 'users', uid));
    } else {
      throw error;
    }
  }
}

// Sync user claims (simplified since Cloud Functions aren't working)
export async function syncUserClaims(uid: string): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No authenticated user');
  }

  // Just force token refresh
  await user.getIdToken(true);
}

export async function getOrganizationUsers(organizationId: string): Promise<User[]> {
  const q = query(
    collection(db, 'users'),
    where('organizationId', '==', organizationId),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ ...doc.data() } as User));
}

export async function getAllUsers(): Promise<User[]> {
  const q = query(
    collection(db, 'users'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ ...doc.data() } as User));
}

// W9 Document Management
export async function uploadW9(userId: string, file: File): Promise<{ url: string; fileName: string }> {
  const currentAuth = getAuth();
  const currentUser = currentAuth.currentUser;

  if (!currentUser) {
    throw new Error('No authenticated user');
  }

  // Create a unique filename with timestamp
  const timestamp = Date.now();
  const fileName = `${timestamp}_${file.name}`;
  const storagePath = `w9-documents/${userId}/${fileName}`;

  // Upload to Firebase Storage
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);

  // Get the download URL
  const downloadUrl = await getDownloadURL(storageRef);

  // Update user document with W9 info
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    w9DocumentUrl: downloadUrl,
    w9UploadedAt: Timestamp.now(),
    w9FileName: fileName,
    updatedAt: Timestamp.now()
  });

  return { url: downloadUrl, fileName };
}

export async function deleteW9(userId: string): Promise<void> {
  const currentAuth = getAuth();
  const currentUser = currentAuth.currentUser;

  if (!currentUser) {
    throw new Error('No authenticated user');
  }

  // Get current user data to find the file path
  const userData = await getUserById(userId);
  if (!userData?.w9FileName) {
    throw new Error('No W9 document found');
  }

  // Delete from Firebase Storage
  const storagePath = `w9-documents/${userId}/${userData.w9FileName}`;
  const storageRef = ref(storage, storagePath);

  try {
    await deleteObject(storageRef);
  } catch (error: any) {
    // If file doesn't exist in storage, continue to clear Firestore
    if (error.code !== 'storage/object-not-found') {
      throw error;
    }
  }

  // Clear W9 fields from user document
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    w9DocumentUrl: null,
    w9UploadedAt: null,
    w9FileName: null,
    updatedAt: Timestamp.now()
  });
}

// Export all functions as a service object for convenience
export const userService = {
  createUser,
  getUsers,
  getUserById,
  updateUser,
  toggleUserStatus,
  bulkUpdateUsers,
  sendPasswordReset,
  setUserPassword,
  deleteUser,
  syncUserClaims,
  getOrganizationUsers,
  getAllUsers,
  uploadW9,
  deleteW9
};
